import { DefaultMap } from '../defaultMap';
import { PASSTHROUGH, doubleQuote, mapToCase, singleQuote } from '../sql';
import assertNever from '@watershed/shared-util/src/assertNever';
import { ASTExpr, ASTProgram, stringifyExpr } from './ast';
import { typecheckExpression } from './typecheck';
import { Context, SqlExpression, SqlTrace } from './types';
import dedent from 'ts-dedent';
import { UserInputError } from '@watershed/errors/UserInputError';
import { safeMaxStage } from './utils';
import { getFunction } from './functions';
import {
  PROGRAM_TABLE,
  PrivateSqlState,
  assertNoUnitExpr,
  getState,
  mergeErrors,
  privateSqlState,
  rowIdCol,
  traceHelper,
  rowIdColI,
  PROGRAM_TABLE_I,
  UsedReference,
  debugParamsColI,
} from './sql/internal';
import { Reference } from './utils/gatherReferences';
import { UnexpectedError } from '@watershed/errors/UnexpectedError';
import {
  TraceASTBinary,
  TraceASTBinaryOp,
  TraceASTNumber,
  TraceASTType,
  TraceASTUnary,
  TraceASTUnaryOp,
} from './trace';
import { lineToSQL } from './sql/line';
import {
  Identifier,
  RawSQL,
  /* eslint-disable @watershed/no-dangerous-raw-sql -- DuckDB usage only */
  dangerousRawSql,
  sql,
  sqlF,
  sqlIdentifier,
  sqlJoinF,
} from '../sqlTemplate';
import { sqlCast } from './sql/cast';
import { dateMacros } from '../duckMacros';
import { unitToString } from '../unit';
import { RowType } from './row_types';

function errorCol(s: string): string {
  // Prefix with __ to avoid collisions with user-defined columns
  return `__error_${s}`;
}

// The variables defined by the statements of a program can appear in, among
// other places, a previous term in a SELECT statement, as in:
//     SELECT 1 AS a, 2 * a AS b FROM ...
// We need a way to unambiguously refer to the column "a" in this case, even if
// one or more tables in the FROM clause define a column "a". There is not to my
// knowledge a way to do this! (if it were always from a table, we might write
// `foo.a`). So, next best thing: we rewrite variables every place they might
// appear such that they are unambiguous, *except* for the final return to the
// user.
class ProgramVarNamer {
  private shortenedNames?: Map<string, string>;

  constructor(shouldShorten: boolean) {
    if (shouldShorten) {
      this.shortenedNames = new Map();
    }
  }

  name(name: string): string {
    let nameToUse = name;

    if (this.shortenedNames != null) {
      let shortName = this.shortenedNames.get(name);
      if (shortName == null) {
        shortName = `v${this.shortenedNames.size}`;
        this.shortenedNames.set(name, shortName);
      }
      nameToUse = shortName;
    }

    return doubleQuote(`${PROGRAM_TABLE}.${nameToUse}`);
  }
}

export function withSqlState(
  ctx: Context,
  references: Array<Reference>
): Context {
  const state: PrivateSqlState = {
    inputVariables: new Set(),
    variablesSql: new Map(),
    traces: new Map(),
    references,
    usedReferences: new Map(),
  };
  return {
    ...ctx,
    [privateSqlState]: state,
  };
}

export function exprToSql(ctx: Context, expr: ASTExpr): SqlExpression {
  const state = getState(ctx);
  switch (expr.t) {
    case 'string':
      return {
        expr: singleQuote(expr.v),
        stage: 0,
        unitExpr: null,
        error: null,
        trace: null,
      };
    case 'number':
      // NB this code is duplicated in functions/cast.ts for the common case of
      // casting number literals to units
      return traceHelper<TraceASTNumber>(
        { ty: 'number' },
        {
          // Due to a bug in DuckDB, decimals are encoded into Parquet files in a
          // way that DuckDB cannot itself read. To work around this, and because
          // we want all computations to occur as doubles anyways, cast all
          // literals to doubles.
          // https://github.com/duckdb/duckdb/issues/6044
          expr: `CAST(${expr.v.toString()} AS DOUBLE)`,
          stage: 0,
          unitExpr: null,
          error: null,
        },
        () => ({ t: TraceASTType.TNumber }),
        { valueMattersInFingerprint: true }
      );
    case 'boolean':
      return {
        expr: expr.v ? 'TRUE' : 'FALSE',
        stage: 0,
        unitExpr: null,
        error: null,
        trace: null,
      };
    case 'unary':
      switch (expr.op) {
        case '-':
          const eSql = exprToSql(ctx, expr.expr);
          const ty = typecheckExpression(ctx, expr.expr);
          if (expr.expr.t === 'number') {
            return exprToSql(ctx, {
              t: 'number',
              v: -expr.expr.v,
            });
          }
          return traceHelper<TraceASTUnary>(
            ty,
            {
              expr: '-' + eSql.expr,
              stage: eSql.stage,
              unitExpr: eSql.unitExpr,
              error: eSql.error,
            },
            (sub) => ({
              t: TraceASTType.TUnary,
              o: TraceASTUnaryOp.TNegative,
              e: sub(eSql.trace),
            })
          );
        default:
          assertNever(expr);
      }
    case 'identifier': {
      const v = state.variablesSql.get(expr.name);

      UnexpectedError.invariant(
        v,
        `Lscript variables must have their SQL set before use.`
      );

      return v;
    }
    case 'binary': {
      const lSql = assertNoUnitExpr(exprToSql(ctx, expr.l));
      const rSql = assertNoUnitExpr(exprToSql(ctx, expr.r));
      let sql: string;
      let traceOp: TraceASTBinaryOp;
      switch (expr.op) {
        case '+':
          sql = `CAST(${lSql.expr} + ${rSql.expr} AS DOUBLE)`;
          traceOp = TraceASTBinaryOp.TPlus;
          break;
        case '-':
          sql = `CAST(${lSql.expr} - ${rSql.expr} AS DOUBLE)`;
          traceOp = TraceASTBinaryOp.TMinus;
          break;
        case '*':
          sql = `CAST(${lSql.expr} * ${rSql.expr} AS DOUBLE)`;
          traceOp = TraceASTBinaryOp.TMultiply;
          break;
        case '/':
          // We always want floating point division, even if the expressions
          // are integers
          sql = `CAST(${lSql.expr} * 1.0 / ${rSql.expr} AS DOUBLE)`;
          traceOp = TraceASTBinaryOp.TDivide;
          break;
        default:
          assertNever(expr);
      }
      return traceHelper<TraceASTBinary>(
        typecheckExpression(ctx, expr),
        {
          expr: sql,
          stage: safeMaxStage([lSql, rSql]),
          unitExpr: null,
          error: mergeErrors([lSql.error, rSql.error]),
        },
        (sub) => ({
          t: TraceASTType.TBinary,
          o: traceOp,
          l: sub(lSql.trace),
          r: sub(rSql.trace),
        })
      );
    }
    case 'comparison': {
      const lSql = assertNoUnitExpr(exprToSql(ctx, expr.l));
      const rSql = assertNoUnitExpr(exprToSql(ctx, expr.r));
      let sql: string;
      switch (expr.op) {
        case '==':
          sql = `(${lSql.expr} IS NOT DISTINCT FROM ${rSql.expr})`;
          break;
        case '!=':
          sql = `(${lSql.expr} IS DISTINCT FROM ${rSql.expr})`;
          break;
        case '<':
          sql = `(${lSql.expr} < ${rSql.expr})`;
          break;
        case '<=':
          sql = `(${lSql.expr} <= ${rSql.expr})`;
          break;
        case '>':
          sql = `(${lSql.expr} > ${rSql.expr})`;
          break;
        case '>=':
          sql = `(${lSql.expr} >= ${rSql.expr})`;
          break;
        default:
          assertNever(expr);
      }
      return {
        expr: sql,
        stage: safeMaxStage([lSql, rSql]),
        unitExpr: null,
        error: mergeErrors([lSql.error, rSql.error]),
        trace: null,
      };
    }
    case 'call': {
      const name = expr.name;
      const fn = getFunction(name);
      UserInputError.invariant(
        fn != null,
        `unknown function ${expr.name} in expression ${stringifyExpr(expr)}`
      );
      return fn.toSql(ctx, expr);
    }
    case 'null':
      const ty = typecheckExpression(ctx, expr);
      return {
        expr: sqlCast('NULL', ty),
        stage: 0,
        unitExpr: null,
        error: null,
        trace: null,
      };
    default:
      assertNever(expr);
  }
}

// SQL is very powerful in most ways but very limited in some ways. To work
// around these limitations, each LScript program compiles into *five* phases
// of SQL statements. They *all* must be executed in order for the output to be
// correct:
//
// - The `preamble` is arbitrary SQL code to run before the other queries.  Intended
//   for defining macros and utility SQL.
// - The `preflightStatement` checks invariants about the input tables. If this
//   query returns any errors, the behavior of running `statement` and
//   `errorStatement` is undefined (put another way, it is run "first," though
//   nothing bad will happen if you run and ignore the results of the subsequent
//   two queries if you later find out the preflight failed).
// - The `stages` are actually the main body of the LScript program. This is where
//   the bulk of the work happens. Each stage is a SQL query that produces a new
//   table. The first stage is the input table, and each subsequent stage is
//   derived from the previous stage. This will also drop previous stages from
//   the database, so the final stage is the only one that will be left in the
//   database after the program is run.
// - The `statement` produces a candidate evaluation result for the LScript
//   program, which is just the main select from the final stage.
// - The `errorStatement` is run against the candidate evaluation result (from
//   running `statement`). If the `errorStatement` does not return any errors,
//   the evaluation result is validated and can be used. If `errorStatement`
//   returns errors, the behavior of the evaluation result is undefined.
//
// A validated evaluation result will contain:
// - One output row per row of input, in the same order as the rows in the input
// - One column per assignment in the program, named after the variable that was
//   assigned. The behavior is undefined if the program assigns the same
//   variable more than once (put another way, the LScript program must be
//   static single-assignment, or SSA).
export type SqlQuery = {
  preflightStatement: string;
  statement: string;
  errorStatement: string;
  inputs: Set<string>;
  preamble: Array<string>;
  stages: Array<string>;
};

export type ErrorFreeSqlQuery = Omit<
  SqlQuery,
  'preflightStatement' | 'errorStatement'
>;

const EMPTY_ERROR_STATEMENT = `SELECT * FROM (VALUES (200, 'ok')) t(row_number, error) WHERE FALSE`;
const EMPTY_PREFLIGHT_STATEMENT = `SELECT '' AS error WHERE FALSE`;
export const SQL_RESULT_TABLE_NAME = 'result';

const TRACE_SQL_COLUMN_PREFIX = '__trace__';

/**
 * Indicates whether a column name represents the trace of another column.
 */
export function isTraceSqlColumnName(col: string): boolean {
  return col.startsWith(TRACE_SQL_COLUMN_PREFIX);
}

/**
 * Returns a SQL select statement that converts encoded values in unit columns
 * (e.g. legacy PV2 units) to their canonical string representations. This
 * should be used to standardize units at the edges of SQL evaluation, i.e. when
 * loading input data and reference data tables, such that the body of the eval
 * only has to deal with clean units.
 */
export function convertEncodedUnits(
  tableExpr: RawSQL,
  rowType: RowType
): RawSQL {
  const unitColumnExprs = new Map<string, RawSQL>();

  for (const type of rowType.values()) {
    if (type.ty === 'quantity') {
      const unitMappings = new Map<string, string>();
      for (const [encodedValue, unit] of type.encodedValuesToUnits) {
        const canonicalStr = unitToString(unit);
        // Skip any values that match the canonical representation
        if (encodedValue !== canonicalStr) {
          unitMappings.set(encodedValue, unitToString(unit));
        }
      }

      if (unitMappings.size > 0) {
        const caseExpr = sqlF`${dangerousRawSql(
          mapToCase(unitMappings, type.column, PASSTHROUGH)
        )} AS ${sqlIdentifier(type.column)}`;

        unitColumnExprs.set(type.column, caseExpr);
      }
    }
  }

  const unitCols = Array.from(unitColumnExprs.keys());
  if (unitCols.length === 0) {
    return tableExpr;
  }

  return sqlF`
    SELECT
      * EXCLUDE (${sqlJoinF(
        unitCols.map((x) => sqlF`${x}`),
        ','
      )}),
      ${sqlJoinF(Array.from(unitColumnExprs.values()), ',')}
    FROM (${tableExpr})
  `;
}

export function programToSql(
  ctx: Context,
  prog: ASTProgram,
  input: string,
  references: Array<Reference>,
  options?: {
    shortenVariableNames?: boolean;
  }
): SqlQuery {
  ctx = withSqlState(ctx, references);
  const state = getState(ctx);
  const inputTableIdentifier = sqlIdentifier(ctx.inputTable);
  const inputTableRowIdIdentifier = rowIdColI(ctx.inputTable);
  const inputRowId = rowIdCol(ctx.inputTable);

  // In order to enforce our semantics that every input row gets exactly one
  // output, we have to give each input row a unique ID, which we later use to
  // look for duplicated rows. They could in principle be random (e.g., UUIDs),
  // but row numbers make for nicer error messages and don't seem to affect
  // performance.
  const statements: Array<string> = [];
  const inputTable = sqlF`
    SELECT *, ROW_NUMBER() OVER () AS ${inputTableRowIdIdentifier}
    FROM ${sqlIdentifier(input)}
  `;

  statements.push(
    sql`CREATE OR REPLACE TEMP TABLE ${inputTableIdentifier} AS (${convertEncodedUnits(
      inputTable,
      ctx.inputType
    )})`
  );

  const programVar = new ProgramVarNamer(
    options?.shortenVariableNames ?? false
  );

  // Evaluate the lines of the program here. This step has some side effects
  // (e.g., setting CTEs and joins in the context state), so has to be done now.
  // This isn't the best spot to work these lines into the query, though, so we
  // accumulate them until later.
  const linesByStage = new DefaultMap<number, Array<string>>(() => []);
  const errorsByLine: Map<string, string> = new Map();
  let stage = 1;
  for (const line of prog) {
    const [eSql, lineTrace] = lineToSQL(ctx, line);

    stage = safeMaxStage([stage, eSql]);
    linesByStage
      .get(stage)
      .push(`${eSql.expr} AS ${programVar.name(line.name)}`);

    if (eSql.error) {
      const eCol = doubleQuote(errorCol(line.name));
      const err = eSql.error;
      linesByStage
        .get(stage)
        .push(
          `CASE WHEN ${err.exists} THEN ${err.expr} ELSE NULL END AS ${eCol}`
        );
      errorsByLine.set(line.name, eCol);
    }

    const vSql: SqlExpression = {
      expr: programVar.name(line.name),
      stage,
      unitExpr: null,
      error: null,
      trace: lineTrace,
    };
    state.variablesSql.set(line.name, vSql);
    if (eSql.trace) {
      // If the value is null, the trace is invalid, because only numbers can be
      // traced. We could check this for every subexpression but that's sort of
      // expensive and annoying; this will suffice!
      const wrappedTrace: SqlTrace = {
        expr: sqlF`CASE WHEN ${vSql.expr} IS NULL THEN NULL ELSE ${eSql.trace.expr} END`,
        fingerprintExpr: sqlF`CASE WHEN ${vSql.expr} IS NULL THEN NULL ELSE ${eSql.trace.fingerprintExpr} END`,
      };
      state.traces.set(line.name, wrappedTrace);
    }
    ctx.variablesType.set(
      line.name,
      line.typeDeclaration ?? typecheckExpression(ctx, line.expr)
    );
  }

  // Evaluating the program has, as a side effect, populated usedReferences.
  // Collate them by stage so we can use them later
  const referencesByStage = new DefaultMap<number, Array<UsedReference>>(
    () => []
  );
  for (const reference of state.usedReferences.values()) {
    referencesByStage.get(reference.sql.stage).push(reference);
  }

  // Working in stages, assemble CTEs for each stage. Stage 0 is the inputs.
  const inputStr = Array.from(state.inputVariables)
    .map((v) => `, ${v} AS ${doubleQuote(`${ctx.inputTable}.${v}`)}`)
    .join('');
  statements.push(dedent`
    CREATE OR REPLACE TEMP TABLE __stage0 AS (
      SELECT ${inputRowId}${inputStr}
      FROM ${ctx.inputTable}
    )
  `);

  for (let i = 1; i <= stage; i++) {
    const references = referencesByStage.get(i);
    const preselects = new Array<string>();
    const joins = new Array<string>();
    let prevTableName = sqlIdentifier(`__stage${i - 1}`);
    let drop: string | undefined = undefined;

    if (references.length > 0) {
      // If there are references, we need to emit N extra statements, one per
      // reference lookup table, plus 1 statement doing the lookup table joins
      // (producing reference data row IDs).
      //
      // We'll do the final join + select against the reference data later.
      //
      // TODO(carl): For awkward control flow reasons, references currently
      // require us to set up a __p view, since they don't know what stage
      // they'll execute as part of, and need a pointer to the "current" stage
      // of execution
      statements.push(
        sql`CREATE OR REPLACE TEMP VIEW ${PROGRAM_TABLE_I} AS SELECT * FROM ${prevTableName}`
      );
      const lutJoins = new Array<RawSQL>();
      const lutSelects = new Array<RawSQL>();
      for (const reference of referencesByStage.get(i)) {
        const name = reference.sql.name;
        // First, calculate the lookup table
        // TODO: drop these tables at an appropriate time. Hopefully not a huge
        // deal, but we don't do it!
        statements.push(reference.sql.lookupTable.table);

        // Then, set up the information we'll need to join the lookup table
        // (populating row IDs)
        lutJoins.push(dangerousRawSql(reference.sql.lookupTable.join));
        lutSelects.push(
          sqlF`${dangerousRawSql(
            reference.sql.lookupTable.expr
          )} AS ${rowIdColI(name)}`
        );
        lutSelects.push(
          sqlF`${dangerousRawSql(
            reference.sql.lookupTable.debugExpr
          )} AS ${debugParamsColI(name)}`
        );

        // Finally, set up the information we'll need to compute the "stage"
        for (const col of reference.columns) {
          preselects.push(
            `${name}.${doubleQuote(col)} AS ${doubleQuote(`${name}.${col}`)}`
          );
        }
        joins.push(`\n${reference.sql.join}`);
      }
      statements.push(sql`DROP VIEW ${PROGRAM_TABLE_I}`);

      const lutTable = sqlIdentifier(`__stage${i}__lut_join`);
      statements.push(
        sql`CREATE OR REPLACE TEMP TABLE ${lutTable} AS SELECT ${PROGRAM_TABLE_I}.*, ${sqlJoinF(
          lutSelects,
          ', '
        )} FROM ${prevTableName} AS ${PROGRAM_TABLE_I} ${sqlJoinF(
          lutJoins,
          ' '
        )}`
      );
      drop = sql`DROP TABLE ${lutTable}`;
      prevTableName = lutTable;
    }

    const selects = linesByStage.get(i);

    statements.push(dedent`
      CREATE OR REPLACE TEMP TABLE __stage${i} AS (
        SELECT
          ${PROGRAM_TABLE}.*,
          ${preselects.concat(selects).join(',\n')}
        FROM ${sql`${prevTableName}`} AS ${PROGRAM_TABLE}${joins.join('')}
      )
    `);
    statements.push(`DROP TABLE __stage${i - 1}`);
    if (drop) {
      statements.push(drop);
    }
  }

  // For our final query, we need to return:
  // - The row IDs of the input and all of the tables we joined against. This is
  //   useful for provenance (e.g., debugging or audit), and to produce useful
  //   error messages when a single input row results in multiple output rows
  // - The output columns, renamed to their normal names (see the note on the
  //   programVar helper)
  const finalSelect: Array<string> = [inputRowId];
  for (const name of state.usedReferences.keys()) {
    finalSelect.push(`${rowIdCol(name)} AS ${doubleQuote(name)}`);
  }

  finalSelect.push(...errorsByLine.values());
  for (const line of prog) {
    finalSelect.push(
      `${programVar.name(line.name)} AS ${doubleQuote(line.name)}`
    );
  }

  const statement = `SELECT ${finalSelect.join(
    ',\n'
  )} FROM __stage${stage} ORDER BY ${inputRowId}`;

  // You can't throw an exception in a SQL query, but some of the stuff we did
  // above (currently: joins, which might not have found a single matching
  // record) could produce errors at runtime. In order to deal with these, we
  // produce a second SQL statement that just returns error messages.
  let errorStatement: string;
  const errorRowFilters: Array<string> = [];
  const errorTerms = Array.from(errorsByLine.entries());

  if (errorsByLine.size > 0) {
    errorStatement = dedent`
      SELECT
        ${inputRowId} AS row_number,
        COALESCE(
          ${errorTerms
            .map(
              ([lineName, e]) =>
                `CASE WHEN ${e} IS NOT NULL THEN ${singleQuote(
                  lineName
                )} ELSE NULL END`
            )
            .join(',\n')}
        ) AS path,
        COALESCE(
          ${errorTerms.map(([_, e]) => `CAST(${e} AS VARCHAR)`).join(',\n')}
        ) AS error
      FROM ${SQL_RESULT_TABLE_NAME}
      WHERE ${inputRowId} IN (
        SELECT ${inputRowId}
        FROM ${SQL_RESULT_TABLE_NAME}
        WHERE ${
          errorRowFilters.length > 0 ? errorRowFilters.join(' OR ') : 'TRUE'
        }
      ) AND error IS NOT NULL
    `;
  } else {
    errorStatement = EMPTY_ERROR_STATEMENT;
  }

  const preflightStatement: string = EMPTY_PREFLIGHT_STATEMENT;

  const inputs = new Set<string>();
  for (const reference of state.references) {
    if (state.usedReferences.has(reference.name)) {
      inputs.add(reference.slug);
    }
  }

  return {
    preflightStatement,
    statement,
    errorStatement,
    preamble: dateMacros,
    stages: statements,
    inputs,
  };
}

export type OriginalExecution = {
  program: ASTProgram;
  references: Array<Reference>;
  // A SQL table that contains the input to the program when it was originally
  // run. For emissions models, this is the post-expansion, post-proration,
  // post-BAT-matcher-filtering parquet file (EMTA).
  inputTable: Identifier;
  // A SQL table that contains the output of the program when it was
  // originally run. For emissions models, this is the emissions model
  // evaluation (EME).
  outputTable: Identifier;
};

// An addendum is any additional calculation we want to run in the context of an
// existing LScript evaluation. All of the variables and references available to
// the original evaluation are available in the addendum context, including
// extra columns from either the input or any of the references. Introducing new
// references is not allowed.
//
// An example use case would be calculating the value of a sub-expression used
// in the original evaluation, or returning a "trace" (see TraceAST / trace.ts)
// describing how a calculation was performed.
//
// These are things we could have, in principle, calculated as part of the
// original evaluation; this function helps us "travel back in time" and act as
// if we'd figured it out on day one.
function addendumToSql(
  ctx: Context,
  original: OriginalExecution,
  // A function that returns the columns to return as part of the addendum.
  // TODO(carl): this was the most convenient / flexible interface for this
  // callback at the time, since I don't feel like I understand the set of use
  // cases well enough. In the future, we can consider making this more
  // structured. For instance, can this be a *new* LScript program? Can we
  // express all the funny business we do here *in the LScript language*?
  addendumSelections: (ctx: Context) => Map<string, RawSQL>
): ErrorFreeSqlQuery {
  ctx = withSqlState(ctx, original.references);
  const state = getState(ctx);
  const inputTableIdentifier = sqlIdentifier(ctx.inputTable);
  const inputRowIdIdentifier = rowIdColI(ctx.inputTable);

  // First, populate the list of variables (a copy-pasta from programToSql
  // above; perhaps we should find a way to consolidate this behavior). We could
  // in principle do this with less work, but (1) the consistency is nice, and
  // (2) the trace expression in particular is annoying to populate any other
  // way
  for (const line of original.program) {
    const [eSql, lineTrace] = lineToSQL(ctx, line);

    const vSql: SqlExpression = {
      expr: `${PROGRAM_TABLE}.${doubleQuote(line.name)}`,
      stage: 0,
      unitExpr: null,
      error: null,
      trace: lineTrace,
    };
    state.variablesSql.set(line.name, vSql);
    if (eSql.trace) {
      // If the value is null, the trace is invalid, because only numbers can be
      // traced. We could check this for every subexpression but that's sort of
      // expensive and annoying; this will suffice!
      const wrappedTrace: SqlTrace = {
        expr: sqlF`CASE WHEN ${vSql.expr} IS NULL THEN NULL ELSE ${eSql.trace.expr} END`,
        fingerprintExpr: sqlF`CASE WHEN ${vSql.expr} IS NULL THEN NULL ELSE ${eSql.trace.fingerprintExpr} END`,
      };
      state.traces.set(line.name, wrappedTrace);
    }
    ctx.variablesType.set(
      line.name,
      line.typeDeclaration ?? typecheckExpression(ctx, line.expr)
    );
  }

  // Then calculate the addendum values. These need to be calculated here
  // because they might add new used variables to the input or a reference
  const addendum = addendumSelections(ctx);

  // Now that we have all of the used references and the columns needed from
  // both the references and the input, reconstruct the program state (the "__p"
  // table)
  const joins = new Array<RawSQL>();
  const renames = new Map<Identifier, Identifier>();
  const programState = new Array<RawSQL>();
  for (const [name, reference] of state.usedReferences) {
    joins.push(dangerousRawSql(reference.sql.join));
    // Add the row id column under the name it used in the original exec. We do
    // this early because we need the correct names to be in place by the time
    // we do the reference data join
    renames.set(sqlIdentifier(name), rowIdColI(name));

    // Add each column we use on the reference
    for (const col of reference.columns) {
      programState.push(
        sqlF`${sqlIdentifier(name)}.${sqlIdentifier(col)} AS ${sqlIdentifier(
          `${name}.${col}`
        )}`
      );
    }
  }

  for (const v of state.inputVariables) {
    programState.push(
      sqlF`${inputTableIdentifier}.${sqlIdentifier(v)} AS ${sqlIdentifier(
        ctx.inputTable + '.' + v
      )}`
    );
  }

  const stages = new Array<string>();
  const inputTable = sqlF`
    SELECT *, ROW_NUMBER() OVER () AS ${inputRowIdIdentifier}
    FROM ${original.inputTable}
  `;

  stages.push(
    sql`CREATE OR REPLACE TEMP TABLE ${inputTableIdentifier} AS (${convertEncodedUnits(
      inputTable,
      ctx.inputType
    )})`
  );

  const renameSql =
    renames.size > 0
      ? sqlF`* EXCLUDE (${sqlJoinF(
          Array.from(renames.keys()).map((k) => sqlF`${k}`),
          ', '
        )}), ${sqlJoinF(
          Array.from(renames.entries()).map(([k, v]) => sqlF`${k} AS ${v}`),
          ', '
        )}`
      : sqlF`*`;

  const joined = sqlIdentifier('__joined');
  stages.push(
    sql`
      CREATE OR REPLACE TEMP TABLE ${joined} AS (
        WITH __output AS (
          SELECT ${renameSql} FROM ${original.outputTable}
        )
        SELECT *, ${sqlJoinF(programState, ', ')}
        FROM __output AS ${PROGRAM_TABLE_I}
        LEFT JOIN ${inputTableIdentifier} ON ${inputTableIdentifier}.${rowIdColI(
          ctx.inputTable
        )} = ${PROGRAM_TABLE_I}.${inputRowIdIdentifier}
        ${sqlJoinF(joins, '\n')}
      )
    `
  );

  const arms = Array.from(addendum.entries()).map(
    ([k, v]) => sqlF`${v} AS ${sqlIdentifier(k)}`
  );
  const statement = sql`
    SELECT
    ${sqlJoinF(arms, ',\n')}
    FROM ${joined} AS ${PROGRAM_TABLE_I}
  `;

  const inputs = new Set<string>();
  for (const reference of state.references) {
    if (state.usedReferences.has(reference.name)) {
      inputs.add(reference.slug);
    }
  }

  return {
    statement,
    inputs,
    preamble: dateMacros,
    stages,
  };
}

// This is a bit of a temporary type as we work through some backwards
// compatibility between the older duckdb-server interface and the newer
// "operations-based" one, which has an "accelerator" for the `UNION ALL` use
// case
export type TraceQuery = {
  inputs: Set<string>;
  queries: Array<string>;
  finalDataShards: Array<string>;
};

// This function exists for backwards compatibility with code paths that want
// the addendum SQL in the "old" format
export function traceToSqlQuery(query: TraceQuery): ErrorFreeSqlQuery {
  const shardQueries = query.finalDataShards.map(
    (query, i) =>
      sql`CREATE OR REPLACE TEMP TABLE "__shard${i}" AS ${dangerousRawSql(
        query
      )}`
  );
  const shardUnions = sql`${sqlJoinF(
    query.finalDataShards.map((_, i) => sqlF`SELECT * FROM "__shard${i}"`),
    ' UNION ALL '
  )}`;
  return {
    statement: shardUnions,
    inputs: query.inputs,
    // TODO: it's always `dateMacros` -- there's no other use case. So there's
    // probably a better way of getting this information around
    preamble: dateMacros,
    stages: query.queries.concat(shardQueries),
  };
}

/**
 * Returns a SqlQuery for generating trace addendums. When run, the query
 * must produce output conforming to the schema in EMTraceAddendumSchema.ts.
 */
export function traceAddendumSql(
  ctx: Context,
  original: OriginalExecution,
  evaluationId: string,
  variables: Set<string>,
  shouldMaterializeTable?: boolean
): TraceQuery {
  // Rough approach:
  // - Make a "wide" query (two columns per variable), because that's way easier
  //   to do given our current addendum abstractions
  // - Melt the result of that into a "long" format, such that the result has
  //   only a few columns with known names. This will make it easier to query
  //   (and e.g., to `UNION ALL`) with few downsides
  const aSql = addendumToSql(ctx, original, (ctx) => {
    const state = getState(ctx);
    const selections = new Map<string, RawSQL>([
      ['row_id', sqlF`${rowIdColI(ctx.inputTable)}`],
    ]);

    for (const variable of variables) {
      const trace = state.traces.get(variable);
      if (trace) {
        selections.set(`trace_${variable}`, trace.expr);
        selections.set(
          `fingerprint_${variable}`,
          sqlF`md5(${trace.fingerprintExpr})`
        );
      } else {
        selections.set(`trace_${variable}`, sqlF`null`);
        selections.set(`fingerprint_${variable}`, sqlF`null`);
      }
    }

    return selections;
  });

  const queries = Array.from(aSql.stages);
  // Traces are textually really big: 1-3kb of JSON each, and a large emissions
  // model will generate maybe 40 traces per input row (more for our very
  // largest EMs). As of DuckDB 0.9.2, DuckDB does not compress strings in
  // memory at all, so materializing this entire query simultaneously results in
  // us running out of memory.
  //
  // For sufficiently large input tables, we can avoid this by creating a view
  // and materializing it in chunks as we convert the "wide" format into the
  // "long" one. In typical usage, we use a special "accelerated" duckdb-server
  // operation to further reduce memory usage.
  //
  // See https://www.notion.so/watershedclimate/Trace-addendum-computation-131e2b76a3fb802b9bb7e5b34845ff8a.

  queries.push(
    sql`CREATE OR REPLACE TEMP ${dangerousRawSql(
      shouldMaterializeTable ? 'TABLE' : 'VIEW'
    )} __trace_wide AS (${dangerousRawSql(aSql.statement)})`
  );

  const finalDataShards = Array.from(variables).map(
    (variable) =>
      sql`SELECT
        ${evaluationId} AS evaluation_id,
        row_id,
        ${variable} AS variable,
        ${sqlIdentifier(`trace_${variable}`)} AS trace,
        ${sqlIdentifier(`fingerprint_${variable}`)} AS fingerprint,
      FROM __trace_wide
    `
  );

  return {
    inputs: aSql.inputs,
    queries,
    finalDataShards,
  };
}
